import { create } from "zustand";
import { EscapeRoom, TourData } from "../../../Types";
import { useViewerPopupStore } from "../../../state management/zustand/viewer-stores";
import { useTourDataStore } from "../../../api/Zustand";

type timerStoreType = {
  remainingTime: string;
  secs: number;
  status: { started: boolean; paused?: boolean; ended?: boolean };
  setRemainingTime: (timestamp: number) => void;
  setTimer: (durationInMins: number) => void;
  addSecs: (amount: number) => void;
  pauseTimer: (bool: boolean) => void;
};

export const useTimerStore = create<timerStoreType>()((set, get) => ({
  remainingTime: "10:00",
  status: { started: false },
  secs: 0,
  pauseTimer: (bool: boolean) => {
    set((state) => ({ status: { ...state.status, paused: bool } }));
  },
  setRemainingTime: (timeStamp: number) => {
    return;
  },
  addSecs: (amount: number) => {
    const total = get().secs + amount;

    set({ secs: total });
  },

  setTimer: (duration: number) => {
    let durationInMs = duration * 60 * 1000;

    let diff = durationInMs;

    const endBase = Date.now();

    let end = endBase + diff;
    // end = end + secs;

    const interval: NodeJS.Timeout = setInterval(() => {
      const isPaused = get().status.paused;

      const now = new Date().getTime();

      if (diff <= 0) {
        set({ status: { started: true, ended: true, paused: false } });
        return clearInterval(interval);
      } else if (!isPaused) {
        const secs = get().secs * 1000;

        diff = end + secs - now;

        set({ remainingTime: formatTime(diff) });
      } else if (isPaused) {
        console.debug(diff);
        end = Date.now() + diff;
      }
    }, 1000);
    return interval;
  },
}));

export function formatTime(time: number): string {
  // Hours, minutes and seconds

  time = Math.ceil(time / 1000);

  let mins = ~~((time % 3600) / 60);
  let secs = ~~time % 60;

  // Output like "1:01" or "4:03:59" or "123:03:59"
  let ret = "";

  ret += "" + String(mins).padStart(2, "0") + ":" + (secs < 10 ? "0" : "");
  // ret += "" + secs;
  return ret;
}

export function formatSeconds(time: number): string {
  // Calculate minutes and seconds
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;

  // Format the result as MM:SS
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
}

type escapeRoomStoreType = {
  type: 0 | 1 | 2 | 3;
  escapeRoomData?: EscapeRoom;
  setType: (tp?: 0 | 1 | 2 | 3) => void;
  setEscapeRoomData: (er?: EscapeRoom) => void;

  lives: number;
  reduceLives: () => void;

  points: number;
  addPoints: (p: number) => void;

  time: number;
  timeLeft: number;
  secondsModifier: number;
  addSecondToTimer: (n: number) => void;

  started: boolean;
  ended: boolean;
  startEscapeRoom: () => void;
  stopEscapeRoom: () => void;

  answers?: number[];
  beenAsnwered?: number[];
  setBeenAnswered?: (n: number) => void;
};

export const useEscapeRoomDataStore = create<escapeRoomStoreType>()(
  (set, get) => ({
    type: 0,

    setEscapeRoomData: (er?: EscapeRoom) => {
      if (er)
        set({
          escapeRoomData: er,
          type: er.escapeRoomType,
          time: er.escapeRoomTime,
          points: er.escapeRoomScore,
          lives: er.escapeRoomLives,
        });
      else set({ escapeRoomData: undefined, type: 0 });
    },
    setType: (tp?: 0 | 1 | 2 | 3) => {
      if (tp) set({ type: tp });
      else set({ type: 0 });
    },

    lives: 0,
    reduceLives: () => {
      const currLives = get().lives;
      if (currLives > 0) set({ lives: currLives - 1 });
      else set({ lives: 0 });
    },

    points: 0,
    addPoints: (p: number) => set({ points: p }),

    time: 0,
    timeLeft: 0,
    secondsModifier: 0,
    addSecondToTimer: (n: number) =>
      set({ secondsModifier: get().secondsModifier + n }),

    started: false,
    ended: false,
    startEscapeRoom: () => {
      set({ started: true });
      const escapeRoom = get().escapeRoomData;

      if (escapeRoom?.escapeRoomType === 1) {
        set({ timeLeft: escapeRoom.escapeRoomTime! * 1000 });

        const endTime =
          getRoundedTimestamp() + escapeRoom.escapeRoomTime! * 1000;

        const timer = setInterval(() => {
          const now = getRoundedTimestamp();

          const modifier = get().secondsModifier * 1000;

          const diff = endTime + modifier - now;

          if (diff > 0) set({ timeLeft: diff });
          else {
            set({ ended: true, timeLeft: 0 });
            clearInterval(timer);

            const endPopupID = get().escapeRoomData?.endPopupID;
            return useViewerPopupStore.setState({
              isOpen: true,
              id: endPopupID,
            });
          }
        }, 1e3);
        return timer;
      } else return;
    },

    stopEscapeRoom: () => set({ ended: true }),
  })
);

function getRoundedTimestamp() {
  const now = Date.now();

  return Math.floor(now / 1000) * 1000;
}

type mockEscapeRoomStoreType = {
  answeredQuestionsInScene: number[];

  hardLockQuestions: (number | undefined)[];
  softLockQuestions: (number | undefined)[];

  setRequiredQuestions: (id: number | undefined, tourData: TourData) => void;
  whipeRequiredQuestions: () => void;
  removeQuestion: (id: number) => void;
  answeredQuestions: number[];
  answeredCorrectly: number[];
  answeredWrongly: number[];

  setAnswered: (questionID: number, correct: boolean) => void;
};

export const useMockEscapeRoomStore = create<mockEscapeRoomStoreType>()(
  (set, get) => ({
    answeredQuestionsInScene: [],

    hardLockQuestions: [],
    softLockQuestions: [],

    setRequiredQuestions: (id: number | undefined, tourData: TourData) => {
      const { quizData } = tourData;

      const answeredQuestions = get().answeredQuestions;

      if (answeredQuestions.includes(id!)) {
        return;
      }

      const currentQuestionFLG = quizData?.data.find(
        (one) => one.questionId === id
      )?.mandatory;

      if (currentQuestionFLG === 1) {
        const currentSoftLock = get().softLockQuestions;
        const updatedSoftLock = [...new Set([...currentSoftLock, id])];
        set({ softLockQuestions: updatedSoftLock });
      } else if (currentQuestionFLG === 2) {
        const currentHardLock = get().hardLockQuestions;
        const updatedHardLock = [...new Set([...currentHardLock, id])];
        set({ hardLockQuestions: updatedHardLock });
      }
    },

    removeQuestion: (id: number) => {
      const hardLock = get().hardLockQuestions;
      const softLock = get().softLockQuestions;
      const answered = get().answeredQuestions;

      const filterHardLock = hardLock.filter((qu) => qu !== id);
      const filteredSoftLock = softLock.filter((qu) => qu !== id);

      set({
        hardLockQuestions: filterHardLock,
        softLockQuestions: filteredSoftLock,
        answeredQuestions: [...new Set([...answered, id])],
      });
    },

    answeredQuestions: [],

    whipeRequiredQuestions: () => {
      set({
        softLockQuestions: [],
        hardLockQuestions: [],
      });
    },

    answeredCorrectly: [],
    answeredWrongly: [],

    setAnswered: (questionID: number, correct: boolean) => {
      const currentAnsweredQ = get().answeredQuestionsInScene;
      set({ answeredQuestionsInScene: [...currentAnsweredQ, questionID] });

      if (correct === true) {
        set({
          answeredCorrectly: [
            ...new Set([...get().answeredCorrectly, questionID]),
          ],
          answeredWrongly: [
            ...new Set([
              ...get().answeredWrongly.filter((one) => one !== questionID),
            ]),
          ],
        });
      } else if (correct === false) {
        set({
          answeredWrongly: [...new Set([...get().answeredWrongly, questionID])],
          answeredCorrectly: [
            ...new Set([
              ...get().answeredCorrectly.filter((one) => one !== questionID),
            ]),
          ],
        });
      }
    },
  })
);
